import React, { useEffect, useCallback, useRef, useState } from 'react';
import {
  Grid,
  Typography,
  TextField,
  Button,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableFooter,
  TableRow,
  Paper,
  TablePagination,
  Autocomplete,
  IconButton,
  Snackbar,
  Alert,
  Popover,
  CircularProgress
} from '@mui/material';
import {
  Clear,
  ArrowUpward,
  ArrowDownward,
  FirstPage,
  LastPage,
  NavigateBefore,
  NavigateNext,
  Add,
  Delete
} from '@mui/icons-material';
import './SupplierGridView.css';

const SupplierMappingPaginationActions = ({ page, count, rowsPerPage, onPageChange }) => {
  const lastPage = Math.max(0, Math.ceil(count / rowsPerPage) - 1);

  const handleFirstPage = (event) => {
    onPageChange(event, 0);
  };

  const handleBackPage = (event) => {
    onPageChange(event, Math.max(page - 1, 0));
  };

  const handleNextPage = (event) => {
    onPageChange(event, Math.min(page + 1, lastPage));
  };

  const handleLastPage = (event) => {
    onPageChange(event, lastPage);
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <IconButton onClick={handleFirstPage} disabled={page === 0} size="small" aria-label="first page">
        <FirstPage />
      </IconButton>
      <IconButton onClick={handleBackPage} disabled={page === 0} size="small" aria-label="previous page">
        <NavigateBefore />
      </IconButton>
      <IconButton onClick={handleNextPage} disabled={page >= lastPage} size="small" aria-label="next page">
        <NavigateNext />
      </IconButton>
      <IconButton onClick={handleLastPage} disabled={page >= lastPage} size="small" aria-label="last page">
        <LastPage />
      </IconButton>
    </Box>
  );
};

const SupplierGridView = ({
  locations,
  suppliers,
  filteredDataList,
  handleCardClick,
  selectedSupplier,
  selectedLocation,
  handleSupplierChange,
  handleClearFilters,
  sortCriteria,
  setSortCriteria,
  sortOrder,
  setSortOrder,
  currentPage,
  totalPages,
  handlePageChange,
  totalDocuments,
  pageLimit,
  selectedDocStatuses,
  deleteSupplierMapping,
  docStatuses,
  dumpSuppliers,
  dumpInProgress,
  processedDocs,
  setProcessedDocs,
  totalDocs,
  setTotalDocs,
  showJobBox,
  setShowJobBox,
  exportDocsCount,
  setExportDocsCount,
  sortedData,
  setSortedData,
  statusMessage,
  setStatusMessage,
  searchTerm,
  onSearchChange,
  showImportButton = true,
  createButtonLabel = null,
  isDataLoading = false,
  isAdmin = false,
  isReadOnly = false
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [pageInput, setPageInput] = useState(String(currentPage));
  const [hoveredHeader, setHoveredHeader] = useState(null);
  const [hasFilters, setHasFilters] = useState(false);
  const [supplierMappingId, setSupplierMappingId] = useState(null);
  const [displayedData, setDisplayedData] = useState([]);
  const [hasFetchedData, setHasFetchedData] = useState(false);
  const formatCellValue = useCallback((value) => {
    if (value === null || value === undefined) {
      return '-';
    }
    const stringValue = typeof value === 'string' ? value.trim() : value;
    if (typeof stringValue === 'string' && stringValue.length === 0) {
      return '-';
    }
    return stringValue;
  }, []);
  const formatAddress = useCallback((value = '') => {
    const base = formatCellValue(value);
    if (base === '-') return base;
    const text = base.toString();
    return text.length > 40 ? `${text.slice(0, 37)}...` : text;
  }, [formatCellValue]);

  const handleFirstPageButtonClick = useCallback(() => {
    handlePageChange(1);
  }, [handlePageChange]);

  const handleBackButtonClick = useCallback(() => {
    if (currentPage > 1) {
      handlePageChange(Math.max(currentPage - 1, 1));
    }
  }, [currentPage, handlePageChange]);

  const handleNextButtonClick = useCallback(() => {
    if (currentPage < totalPages) {
      handlePageChange(Math.min(currentPage + 1, totalPages));
    }
  }, [currentPage, handlePageChange, totalPages]);

  const handleLastPageButtonClick = useCallback(() => {
    if (totalPages > 0) {
      handlePageChange(totalPages);
    }
  }, [handlePageChange, totalPages]);

  const startDocNum = (currentPage - 1) * pageLimit + 1;
  const endDocNum = Math.min(currentPage * pageLimit, totalDocuments);

  useEffect(() => {
    setPageInput(String(currentPage));
  }, [currentPage]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'ArrowLeft' && currentPage > 1) {
        handleBackButtonClick();
      }
      if (event.key === 'ArrowRight' && currentPage < totalPages) {
        handleNextButtonClick();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentPage, totalPages, handleBackButtonClick, handleNextButtonClick]);

  const previousSupplierRef = useRef(selectedSupplier);
  const previousLocationRef = useRef(selectedLocation);

  useEffect(() => {
    if (
      previousSupplierRef.current !== selectedSupplier ||
      previousLocationRef.current !== selectedLocation
    ) {
      if (currentPage !== 1) {
        handleFirstPageButtonClick();
      }
      setSortCriteria(null);
      setSortOrder(null);
    }
    previousSupplierRef.current = selectedSupplier;
    previousLocationRef.current = selectedLocation;
  }, [
    selectedSupplier,
    selectedLocation,
    currentPage,
    handleFirstPageButtonClick,
    setSortCriteria,
    setSortOrder,
  ]);

  const handleDialogOpen = (supplier_mapping_id, event) => {
    setSupplierMappingId(supplier_mapping_id);
    setAnchorEl(event.currentTarget);
  };

  const handlePageInputChange = (event) => {
    const numericValue = event.target.value.replace(/[^0-9]/g, '');
    setPageInput(numericValue);
  };

  const handlePageInputKeyDown = (event) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    if (!totalDocuments) {
      setPageInput('1');
      return;
    }
    const desiredPage = Number(pageInput);
    if (!Number.isFinite(desiredPage) || desiredPage < 1) {
      setPageInput(String(currentPage));
      return;
    }
    const clampedPage = Math.min(Math.max(Math.floor(desiredPage), 1), totalPages || 1);
    if (clampedPage !== currentPage) {
      handlePageChange(clampedPage);
    } else {
      setPageInput(String(clampedPage));
    }
  };

  const handleHeaderClick = (criteria) => {
    if (!criteria) return;
    if (sortCriteria === criteria) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortCriteria(criteria);
      setSortOrder('asc');
    }
    setHoveredHeader(null);
  };

  const handleHeaderHover = (criteria) => setHoveredHeader(criteria);

  const closePopover = () => {
    setSupplierMappingId(null);
    setAnchorEl(null);
  };

  const handleConfirmAction = () => {
    deleteSupplierMapping(supplierMappingId);
    setAnchorEl(null);
  };

  useEffect(() => {
    if (!isDataLoading) {
      setDisplayedData(Array.isArray(sortedData) ? sortedData : []);
      setHasFetchedData(true);
    }
  }, [sortedData, isDataLoading]);

  useEffect(() => {
    if (!filteredDataList?.length) {
      setSortedData([]);
      return;
    }
    // Protect against null sortCriteria
    if (!sortCriteria) {
      setSortedData([...filteredDataList]);
      return;
    }
    setSortedData(
      [...filteredDataList].sort((a, b) => {
        let valueA = a[sortCriteria];
        let valueB = b[sortCriteria];

        if (sortCriteria === 'total') {
          valueA = parseFloat(a.total);
          valueB = parseFloat(b.total);
        }

        if (sortCriteria === 'invoice_date' || sortCriteria === 'created_on') {
          valueA = new Date(a[sortCriteria]);
          valueB = new Date(b[sortCriteria]);
        }

        if (valueA < valueB) return sortOrder === 'asc' ? -1 : 1;
        if (valueA > valueB) return sortOrder === 'asc' ? 1 : -1;
        return 0;
      })
    );
  }, [filteredDataList, sortCriteria, sortOrder, setSortedData]);

  useEffect(() => {
    if (showImportButton) {
      setHasFilters(
        selectedSupplier !== '' ||
          selectedLocation !== '' ||
          selectedDocStatuses.some((status) => status !== 4) ||
          !!sortCriteria
      );
    } else {
      setHasFilters(selectedSupplier !== '');
    }
  }, [showImportButton, selectedSupplier, selectedLocation, selectedDocStatuses, sortCriteria]);

  const isInitialLoading = isDataLoading && !hasFetchedData;
  const shouldShowOverlaySpinner = isDataLoading && hasFetchedData;

  return (
    <div>
      <Grid sx={{ py: 3 }} container spacing={showImportButton ? 4 : 2}>
        <Grid
          item
          xs={12}
          md={showImportButton ? 3 : 12}
          style={{
            paddingTop: showImportButton ? '70px' : '0',
            paddingLeft: showImportButton ? undefined : 0,
            paddingRight: showImportButton ? undefined : 0,
          }}
        >
          {showImportButton && (
            <Box className="filterElement">
              <Button onClick={dumpSuppliers} disabled={dumpInProgress} className="pageButton">
                Import Suppliers
              </Button>
            </Box>
          )}

          <Box className={`filterRow ${showImportButton ? '' : 'filterRow--mapping'}`.trim()}>
            <Box className={`filterElement ${showImportButton ? '' : 'filterElementThird'}`.trim()}>
              <Typography variant="subtitle1" className="filterTitle">
                Filter by Supplier
              </Typography>
              <Autocomplete
                options={suppliers}
                value={
                  selectedSupplier
                    ? suppliers.find((supplier) => supplier.supplier_id === selectedSupplier)
                    : null
                }
                getOptionLabel={(option) => {
                  if (!option) return '';
                  const id = option.supplier_id ?? '';
                  const name = option.name ?? '';
                  return id ? `${id} - ${name}` : name;
                }}
                renderOption={(props, option) => (
                  <li {...props}>
                    <span>{option.supplier_id ?? ''}</span>
                    <span style={{ marginLeft: '8px' }}>- {option.name}</span>
                  </li>
                )}
                onChange={(event, newValue) =>
                  handleSupplierChange({
                    target: { value: newValue ? newValue.supplier_id : '' },
                  })
                }
                renderInput={(params) => <TextField {...params} placeholder="All Suppliers" fullWidth />}
              />
            </Box>
            {hasFilters && (
              <Box className="clearButton">
                <Button variant="outlined" startIcon={<Clear />} onClick={handleClearFilters}>
                  Clear Filters
                </Button>
              </Box>
            )}
          </Box>
        </Grid>

        <Grid
          item
          xs={12}
          md={showImportButton ? 9 : 12}
          style={{
            paddingLeft: showImportButton ? undefined : 0,
            paddingRight: showImportButton ? undefined : 0,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              paddingBottom: showImportButton ? '12px' : '16px',
              gap: 2,
            }}
          >
            {!showImportButton && (
              <Typography variant="h5" className="supplier-grid-view__heading">
                Suppliers Mapping
              </Typography>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <TextField
                value={searchTerm}
                onChange={(event) => onSearchChange?.(event.target.value)}
                placeholder="Search suppliers..."
                size="small"
                sx={{ minWidth: 220 }}
              />

              {!isReadOnly && (
                createButtonLabel ? (
                  <Button onClick={() => handleCardClick({})} color="primary" variant="contained" startIcon={<Add />}>
                    {createButtonLabel}
                  </Button>
                ) : (
                  <IconButton onClick={() => handleCardClick({})} color="primary" aria-label="add">
                    <Add />
                  </IconButton>
                )
              )}
            </Box>
          </Box>

          <Box sx={{ position: 'relative' }}>
            <TableContainer component={Paper} style={{ width: '100%' }}>
              <Table style={{ width: '100%' }} size="small" className="supplier-grid-view__table">
                <TableHead>
                  <TableRow>
                    <TableCell
                      className={`sortableHeader ${sortCriteria === 'supplier_id' ? 'sorted' : ''}`}
                      onClick={() => handleHeaderClick('supplier_id')}
                      onMouseEnter={() => handleHeaderHover('supplier_id')}
                      onMouseLeave={() => setHoveredHeader(null)}
                    >
                      <div className="headerContent">
                        <strong>Supplier Id</strong>
                        {(sortCriteria === 'supplier_id' || hoveredHeader === 'supplier_id') &&
                          (sortCriteria === 'supplier_id' ? (
                            sortOrder === 'asc' ? (
                              <ArrowUpward className="sortIcon" />
                            ) : (
                              <ArrowDownward className="sortIcon" />
                            )
                          ) : (
                            <ArrowDownward className="hoverIcon" />
                          ))}
                      </div>
                    </TableCell>

                    <TableCell
                      className={`sortableHeader ${sortCriteria === 'name' ? 'sorted' : ''}`}
                      onClick={() => handleHeaderClick('name')}
                      onMouseEnter={() => handleHeaderHover('name')}
                      onMouseLeave={() => setHoveredHeader(null)}
                    >
                      <div className="headerContent">
                        <strong>Name</strong>
                        {(sortCriteria === 'name' || hoveredHeader === 'name') &&
                          (sortCriteria === 'name' ? (
                            sortOrder === 'asc' ? (
                              <ArrowUpward className="sortIcon" />
                            ) : (
                              <ArrowDownward className="sortIcon" />
                            )
                          ) : (
                            <ArrowDownward className="hoverIcon" />
                          ))}
                      </div>
                    </TableCell>

                    <TableCell
                      className={`sortableHeader ${sortCriteria === 'address' ? 'sorted' : ''}`}
                      onClick={() => handleHeaderClick('address')}
                      onMouseEnter={() => handleHeaderHover('address')}
                      onMouseLeave={() => setHoveredHeader(null)}
                    >
                      <div className="headerContent">
                        <strong>Address</strong>
                        {(sortCriteria === 'address' || hoveredHeader === 'address') &&
                          (sortCriteria === 'address' ? (
                            sortOrder === 'asc' ? (
                              <ArrowUpward className="sortIcon" />
                            ) : (
                              <ArrowDownward className="sortIcon" />
                            )
                          ) : (
                            <ArrowDownward className="hoverIcon" />
                          ))}
                      </div>
                    </TableCell>

                    <TableCell
                      className={`sortableHeader ${sortCriteria === 'telephone' ? 'sorted' : ''}`}
                      onClick={() => handleHeaderClick('telephone')}
                      onMouseEnter={() => handleHeaderHover('telephone')}
                      onMouseLeave={() => setHoveredHeader(null)}
                    >
                      <div className="headerContent">
                        <strong>Telephone</strong>
                        {(sortCriteria === 'telephone' || hoveredHeader === 'telephone') &&
                          (sortCriteria === 'telephone' ? (
                            sortOrder === 'asc' ? (
                              <ArrowUpward className="sortIcon" />
                            ) : (
                              <ArrowDownward className="sortIcon" />
                            )
                          ) : (
                            <ArrowDownward className="hoverIcon" />
                          ))}
                      </div>
                    </TableCell>

                    <TableCell
                      className={`sortableHeader ${sortCriteria === 'email' ? 'sorted' : ''}`}
                      onClick={() => handleHeaderClick('email')}
                      onMouseEnter={() => handleHeaderHover('email')}
                      onMouseLeave={() => setHoveredHeader(null)}
                    >
                      <div className="headerContent">
                        <strong>Email</strong>
                        {(sortCriteria === 'email' || hoveredHeader === 'email') &&
                          (sortCriteria === 'email' ? (
                            sortOrder === 'asc' ? (
                              <ArrowUpward className="sortIcon" />
                            ) : (
                              <ArrowDownward className="sortIcon" />
                            )
                          ) : (
                            <ArrowDownward className="hoverIcon" />
                          ))}
                      </div>
                    </TableCell>

                    <TableCell
                      className={`sortableHeader ${sortCriteria === 'website' ? 'sorted' : ''}`}
                      onClick={() => handleHeaderClick('website')}
                      onMouseEnter={() => handleHeaderHover('website')}
                      onMouseLeave={() => setHoveredHeader(null)}
                    >
                      <div className="headerContent">
                        <strong>Website</strong>
                        {(sortCriteria === 'website' || hoveredHeader === 'website') &&
                          (sortCriteria === 'website' ? (
                            sortOrder === 'asc' ? (
                              <ArrowUpward className="sortIcon" />
                            ) : (
                              <ArrowDownward className="sortIcon" />
                            )
                          ) : (
                            <ArrowDownward className="hoverIcon" />
                          ))}
                      </div>
                    </TableCell>

                    <TableCell sx={{ display: isAdmin ? 'table-cell' : 'none' }}>
                      <div className="headerContent">
                        <strong>Delete</strong>
                      </div>
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {isInitialLoading ? (
                    <TableRow className="tableRow tableRowEmpty">
                      <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                  <CircularProgress size={20} />
                  <Typography color="text.secondary">Loading...</Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : displayedData.length > 0 ? (
                    displayedData.map((data) => (
                      <TableRow key={data.id || data.supplier_id} className="tableRow" style={{ cursor: 'pointer' }}>
                        <TableCell onClick={() => handleCardClick(data)}>{formatCellValue(data.supplier_id)}</TableCell>
                        <TableCell onClick={() => handleCardClick(data)}>{formatCellValue(data.name)}</TableCell>
                        <TableCell onClick={() => handleCardClick(data)}>{formatAddress(data.address)}</TableCell>
                        <TableCell onClick={() => handleCardClick(data)}>{formatCellValue(data.telephone)}</TableCell>
                        <TableCell onClick={() => handleCardClick(data)}>{formatCellValue(data.email)}</TableCell>
                        <TableCell onClick={() => handleCardClick(data)}>{formatCellValue(data.website)}</TableCell>
                        <TableCell sx={{ display: isAdmin ? 'table-cell' : 'none' }}>
                          <IconButton
                            onClick={(e) => handleDialogOpen(data.id, e)}
                            color="error"
                            aria-label="delete"
                            sx={{ display: isAdmin ? 'inline-flex' : 'none' }}
                          >
                            <Delete sx={{ fontSize: 18 }} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow className="tableRow tableRowEmpty">
                      <TableCell colSpan={7} align="center">
                        No record(s) found.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>

                {/* --- FOOTER (pagination as last row, same as Suppliers page) --- */}
                <TableFooter>
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'flex-end',
                          alignItems: 'center',
                          flexWrap: 'wrap',
                          gap: 2,
                          padding: '0px 16px',
                        }}
                      >
                       

                        <TextField
                          value={pageInput}
                          onChange={handlePageInputChange}
                          onKeyDown={handlePageInputKeyDown}
                          label="Page"
                          size="small"
                          sx={{ width: 90 }}
                          inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                          disabled={isInitialLoading || !totalDocuments}
                        />

                        <Typography variant="body2">
                          {!isInitialLoading && totalDocuments ? `of ${totalPages}` : ''}
                        </Typography>
                         <Typography variant="body2">
                          {totalDocuments
                            ? `Showing ${startDocNum}-${endDocNum} of ${totalDocuments}`
                            : hasFetchedData
                              ? 'No supplier mappings to display'
                              : 'Showing 0-0 of 0'}
                        </Typography>
                        <TablePagination
                          component="div"
                          count={totalDocuments || 0}
                          page={Math.max(0, (currentPage || 1) - 1)}                 // MUI expects 0-based
                          onPageChange={(_, p) => handlePageChange(p + 1)}           // convert back to 1-based
                          rowsPerPage={pageLimit}
                          rowsPerPageOptions={[pageLimit]}
                          labelRowsPerPage=""
                          labelDisplayedRows={() => ''}
                          ActionsComponent={(paginationProps) => (
                            <SupplierMappingPaginationActions
                              page={paginationProps.page}
                              count={paginationProps.count}
                              rowsPerPage={paginationProps.rowsPerPage}
                              onPageChange={paginationProps.onPageChange}
                            />
                          )}
                          sx={{
                            '.MuiTablePagination-toolbar': { p: 0 },
                            '.MuiTablePagination-selectLabel': { display: 'none' },
                            '.MuiTablePagination-displayedRows': { m: 0 },
                          }}
                        />
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
            {shouldShowOverlaySpinner && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(255, 255, 255, 0.55)',
                  pointerEvents: 'none',
                }}
              >
                <CircularProgress size={20} />
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>

      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        open={!!statusMessage.message}
        autoHideDuration={6000}
        onClose={() => setStatusMessage({ type: '', message: '' })}
      >
        <Alert onClose={() => setStatusMessage({ type: '', message: '' })} severity={statusMessage.type} sx={{ width: '100%' }}>
          {statusMessage.message}
        </Alert>
      </Snackbar>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={closePopover}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Box p={2}>
          <Box mt={1} display="flex" justifyContent="space-between">
            <Button style={{ marginRight: '10px' }} variant="outlined" onClick={closePopover}>
              Cancel
            </Button>
            <Button variant="contained" color="error" onClick={handleConfirmAction}>
              Confirm
            </Button>
          </Box>
        </Box>
      </Popover>
    </div>
  );
};

export default SupplierGridView;
